import TAG_ADJECTIVES from "Constants/tagAdjectives";
import { PLATFORMS } from "Constants/platforms";

import {ApiService, NotixApi} from "Services/api";
import I18nService from "Services/i18n";
import { delayPromise } from "../utils/delayPromise";

const MAX_REQUEST_COUNT = 10;

export const RATE_MODEL = {
  CPM: 1,
  CPS: 7,
};

export const RATE_MODEL_LABEL = [
  {
    id: String(RATE_MODEL.CPM),
    label: "Rev. share",
    value: String(RATE_MODEL.CPM),
  },
  {
    id: String(RATE_MODEL.CPS),
    label: "CPS",
    value: String(RATE_MODEL.CPS),
  },
];

export const DIRECTIONS = {
  BANNER: "banner",
  DIRECT: "direct",
  INTERSTITIAL: "interstitial",
  MEDIABUY: "mediabuy",
  MOBBANER: "mobbanner",
  MOBNOTICE: "mobnotice",
  NATIVEADS: "nativeads",
  NOTICE: "notice",
  NOTIX: "notix",
  ONCLICK: "onclick",
  ONCLICK5: "onclick5",
  PUSHUP: "pushup",
  DIRECT_ONCLICK: "direct_onclick",
  PUSHER_PPS: "pusherpps",
  NATIVE: "native",
  DIRECT_NATIVEADS: "directnativeads",
  DIRECT_PUSHERPPS: "directpusherpps",
};

export const DIRECTION_LABELS = {
  [DIRECTIONS.BANNER]: "Banner",
  [DIRECTIONS.DIRECT]: "Direct link classic",
  [DIRECTIONS.DIRECT_ONCLICK]: "Direct link classic",
  [DIRECTIONS.INTERSTITIAL]: "Interstitial",
  [DIRECTIONS.MEDIABUY]: "Mediabuy",
  [DIRECTIONS.MOBBANER]: "Mobile Banner",
  [DIRECTIONS.MOBNOTICE]: "Mobile Notice",
  [DIRECTIONS.NATIVEADS]: "Push Notifications",
  [DIRECTIONS.NOTICE]: "Notice",
  [DIRECTIONS.ONCLICK]: "TrafficBack",
  [DIRECTIONS.ONCLICK5]: "OnClick (Popunder)",
  [DIRECTIONS.PUSHUP]: "Dialog Ads / Push Up",
  [DIRECTIONS.NATIVE]: "Native Banner",
};

export const ZONES_DATA = {
  CREATED_AT: "createdAt",
  SUBSCRIBERS: "subscribers",
};

// app_type of zones created for the removed Notix InApp feature.
export const APP_TYPE_INAPP = 2;

// isInAppZone detects zones of the removed Notix InApp feature; such zones
// still exist in ADP and must be hidden from the UI. ADP endpoints expose
// app_type under customSettings, notix ones under zoneSettings.
export function isInAppZone(zone) {
  return (
    zone?.customSettings?.app_type === APP_TYPE_INAPP ||
    zone?.zoneSettings?.app_type === APP_TYPE_INAPP
  );
}

export function generateZoneTitle() {
  return TAG_ADJECTIVES[Math.floor(Math.random() * TAG_ADJECTIVES?.length)];
}

export function getTranslateForLabel(item) {
  switch (item.id) {
    case String(RATE_MODEL.CPM):
      return I18nService.getStringByKey("rate-model-cpm");
    case String(RATE_MODEL.CPS):
      return I18nService.getStringByKey("rate-model-cps");
    default:
      return item.label;
  }
}

class Zones {
  getZone(siteId, zoneId) {
    return ApiService.get(`/sites/${siteId}/zones/${zoneId}/`)
      .then((zone) => {
        return this.prepareZoneToClient(zone);
      })
      .catch((errors) => errors);
  }

  prepareZoneToClient(zone) {
    const defaultRateModelId =
      zone.defaultRateModelId === null
        ? RATE_MODEL.CPM
        : zone.defaultRateModelId;

    return {
      ...zone,
      defaultRateModelId,
      defaultRateModelLabel: this.getRateModelLabel(defaultRateModelId),
    };
  }

  getRateModelLabel(rateModelId) {
    const rateModel = RATE_MODEL_LABEL.find((item) => {
      return item.value === String(rateModelId);
    });

    return rateModel ? rateModel.label : "Undefined";
  }

  getPlatformName(id) {
    return PLATFORMS.find((item) => item.value === id).label;
  }

  createNotixZone(siteId, title, options) {
    return this.createZone(
      siteId,
      title,
      DIRECTIONS.NOTIX,
      RATE_MODEL.CPM,
      options
    );
  }

  createZone(siteId, title, direction, rateModel, options = {}) {
    const preparedData = {
      direction,
      title,
      rate_model: rateModel,
      ...options,
    };
    return ApiService.post(`/sites/${siteId}/zones/`, preparedData);
  }

  getAllZones(directions = [], publisher_sites = []) {
    return ApiService.get("/zones/", {
      params: { directions, publisher_sites },
    }).then((zones) => {
      return zones
        .filter((zone) => !isInAppZone(zone))
        .map((zone) => {
          return {
            ...zone,
            title: `${zone.id} | ${zone.title} | ${zone.siteUrl}`,
          };
        });
    });
  }

  renameZone(siteId, zoneId, title) {
    const preparedZone = {
      title,
    };

    return ApiService.put(`/sites/${siteId}/zones/${zoneId}/`, preparedZone);
  }

  editNotixZoneSettings(zoneId, zoneSettings) {
    return NotixApi.post(`/tag-settings?zoneId=${zoneId}`, zoneSettings);
  }

  tagSettingsCount = 0;

  async getNotixTagSettings(zoneId) {
    let result = await NotixApi.get(`/tag-settings?zoneId=${zoneId}`);
    if (
      Array.isArray(result) &&
      !result?.length &&
      this.tagSettingsCount < MAX_REQUEST_COUNT
    ) {
      this.tagSettingsCount = this.tagSettingsCount + 1;
      await delayPromise(2000);
      result = await this.getNotixTagSettings(zoneId);
    }
    return result;
  }

  getAdvZones() {
    return NotixApi.get(`/adv-zones`)
      .then((zones) => {
        return zones;
      })
      .catch((errors) => errors);
  }
}

const zonesInstance = new Zones();

export default zonesInstance;